import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Globe, LogIn, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/clarity-zones-logo.png";

export default function VoucherLogin() {
  const { t, toggleLang } = useI18n();
  const { loginVoucher } = useAuth();
  const navigate = useNavigate();

  const [alias, setAlias] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!alias.trim() || !password.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/voucher/login`, {
        alias: alias.trim(),
        password: password.trim(),
      });
      loginVoucher(res.data);
      navigate("/assessment");
    } catch (err) {
      const msg = err.response?.data?.detail || t("voucher_invalid");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="voucher-login-page">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-3">
          <img src={LOGO_URL} alt="Clarity Zones" className="h-9 w-auto" data-testid="login-logo" />
          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors px-3 py-1.5 rounded-sm hover:bg-slate-100"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{t("lang_toggle")}</span>
            </button>
            <button
              data-testid="admin-link"
              onClick={() => navigate("/admin")}
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 transition-colors px-2 py-1.5 text-xs"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Admin
            </button>
          </div>
        </div>
      </header>

      {/* Login form */}
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <img
              src={LOGO_URL}
              alt="Clarity Zones"
              className="h-14 w-auto mx-auto mb-6"
              data-testid="voucher-login-logo-center"
            />
            <h1 className="font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-3" data-testid="login-title">
              {t("voucher_title")}
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed max-w-sm mx-auto">
              {t("voucher_subtitle")}
            </p>
          </div>

          <form onSubmit={handleLogin} className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700" htmlFor="alias-input">
                {t("voucher_alias_label")}
              </label>
              <Input
                id="alias-input"
                data-testid="alias-input"
                type="text"
                placeholder={t("voucher_alias_placeholder")}
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                className="h-12 border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-sm bg-slate-50/50"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700" htmlFor="password-input">
                {t("voucher_password_label")}
              </label>
              <Input
                id="password-input"
                data-testid="password-input"
                type="password"
                placeholder={t("voucher_password_placeholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-sm bg-slate-50/50"
                autoComplete="off"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="voucher-login-button"
              disabled={loading || !alias.trim() || !password.trim()}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm h-12 font-bold tracking-wide text-sm shadow-lg hover:-translate-y-0.5 transition-all duration-200 gap-2"
            >
              <LogIn className="w-4 h-4" />
              {loading ? "..." : t("voucher_login_button")}
            </Button>
          </form>

          <p className="text-xs text-slate-400 text-center mt-6 leading-relaxed max-w-xs mx-auto">
            {t("voucher_hint")}
          </p>
        </div>
      </div>
    </div>
  );
}
